import * as yup from 'yup';
import { startOfDay } from 'date-fns';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddCouponMutation as AddCoupon, CouponType, GetCouponsQuery as GetCoupons } from '__generated__/graphql';
import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddCouponMutation from './AddCouponMutation';
import CouponValueInput from './CouponValueInput';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

type Event = GetCoupons['project']['event'];
type AddedCoupon = AddCoupon['addCoupon'];

const addCouponFormSchema = yup.object().shape({
  prefix: yup.string().min(2).max(50).required(),
  value: yup.number().required().min(0),
});

export interface AddCouponFormProps {
  event: Event;
  onSuccess?: (coupon: AddedCoupon) => void;
}

const AddCouponForm = ({ event, onSuccess }: AddCouponFormProps) => {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      event,
      prefix: '',
      type: CouponType.absolute,
      value: null,
      start_date: formatDate(
        startOfDay(new Date()),
        { format: 'internal_date_time', timezone: 'UTC' },
      ),
      end_date: null,
      max_uses: null,
      notes: null,
      tickets: [],
      products: [],
      active: !event.active,
    },
  });

  const [addCoupon, { error, loading }] = useMutation(AddCouponMutation, {
    variables: {
      input: {
        ...form.values,
        event: { id: form.values.event.id },
        tickets: form.values.tickets.map((ticket) => ({ id: ticket.id })),
        products: form.values.products.map((product) => ({ id: product.id })),
      },
    },
    onCompleted: ({ addCoupon }) => {
      notifier.success(t('notifications.coupon_added'));
      onSuccess?.(addCoupon);
    },
    onError: () => {
      //
    },
  });

  const setType = (type: CouponType) => {
    form.set((values) => {
      let { value } = values;

      if (value && type !== values.type) {
        if (type === CouponType.absolute) {
          value *= 100;
        } else if (type === CouponType.percentage) {
          value = Math.min(Math.round(value / 100), 100);
        }
      }

      return {
        ...values,
        type,
        value,
      };
    });
  };

  const { valid, errors } = useValidation({
    schema: addCouponFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addCoupon()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_coupon')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_coupon_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup
          sc={{ valid: !errors.prefix, touched: form.touched.prefix }}
        >
          <UI.InputLabel htmlFor="CouponPrefix">
            {t('coupon_code')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            id="CouponPrefix"
            {...form.text('prefix')}
            placeholder={t('coupon_code_placeholder')}
            autoFocus
          />
          <UI.ErrorMessages attribute={t('coupon_code')} errors={errors.prefix} />
        </UI.InputGroup>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.type, touched: form.touched.type }}>
            <UI.InputLabel>
              {t('coupon_type')}
            </UI.InputLabel>
            <UI.Radio
              onChange={() => setType(CouponType.absolute)}
              checked={form.values.type === CouponType.absolute}
            >
              {t('absolute_amount')}
            </UI.Radio>
            <UI.Radio
              onChange={() => setType(CouponType.percentage)}
              checked={form.values.type === CouponType.percentage}
            >
              {t('percentage_amount')}
            </UI.Radio>
            <UI.ErrorMessages
              attribute={t('coupon_type')}
              errors={errors.type}
            />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.value, touched: form.touched.value }}>
            <UI.InputLabel htmlFor="CouponValue">
              {t('coupon_value')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <CouponValueInput form={form} />
            <UI.ErrorMessages attribute={t('coupon_value')} errors={errors.value} />
          </UI.InputGroup>
        </UI.FormGrid>
      </UI.FormGrid>

      <UI.HR />

      <UI.FormButtons
        form={form}
        submitLabel={t('add_coupon')}
        loading={loading}
        valid={valid}
      />
    </UI.Form>
  );
};

export default AddCouponForm;
